import { Injectable, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, EMPTY } from 'rxjs';
import { debounceTime, distinctUntilChanged, catchError, finalize } from 'rxjs/operators';
import { UserMasterService } from 'app/modules/components/routing/sub-routes/sub-route-collaborator/user_master.service';

export interface MasterUser {
  _id: string;
  user_login_id: string;
  business_partner_id: string;
  type: string;
  user_login_username: string;
  user_login_first_name: string;
  user_login_last_name: string | null;
  user_login_email: string;
}

@Injectable()
export class UserSelectorModalService {
  private readonly userMasterService = inject(UserMasterService);

  // === ACTIONS (RxJS Subjects) ===
  public readonly setSearch$ = new Subject<string>();
  public readonly setPage$ = new Subject<{ pageIndex: number; pageSize: number }>();
  public readonly toggleUser$ = new Subject<MasterUser>();
  public readonly toggleAllPage$ = new Subject<MasterUser[]>();
  public readonly clearSelection$ = new Subject<void>();
  public readonly retry$ = new Subject<void>();

  // === STATE (Signals) ===
  private readonly searchTerm = signal<string>('');
  private readonly currentPage = signal<number>(1);
  private readonly pageSize = signal<number>(10);
  private readonly usersData = signal<MasterUser[]>([]);
  private readonly totalItems = signal<number>(0);
  private readonly loading = signal<boolean>(false);
  private readonly errorState = signal<Error | null>(null);

  // Selección persistente (Map para acceso O(1))
  private readonly selectedUsersMap = signal<Map<string, MasterUser>>(new Map());

  // === COMPUTED (Public API) ===
  public readonly users = this.usersData.asReadonly();
  public readonly total = this.totalItems.asReadonly();
  public readonly isLoading = this.loading.asReadonly();
  public readonly error = this.errorState.asReadonly();
  public readonly page = this.currentPage.asReadonly();
  public readonly size = this.pageSize.asReadonly();

  // Selección
  public readonly selectedUsers = computed(() =>
    Array.from(this.selectedUsersMap().values())
  );
  public readonly selectedIds = computed(() =>
    Array.from(this.selectedUsersMap().keys())
  );
  public readonly selectedCount = computed(() =>
    this.selectedUsersMap().size
  );

  // Verificar si un usuario está seleccionado
  public isSelected(userId: string | unknown): boolean {
    return this.selectedUsersMap().has(String(userId));
  }

  // Verificar si todos los de la página están seleccionados
  public readonly allPageSelected = computed(() => {
    const users = this.usersData();
    if (users.length === 0) return false;
    return users.every(u => this.selectedUsersMap().has(String(u._id)));
  });

  public readonly somePageSelected = computed(() => {
    const users = this.usersData();
    const selected = this.selectedUsersMap();
    const someSelected = users.some(u => selected.has(String(u._id)));
    const allSelected = users.every(u => selected.has(String(u._id)));
    return someSelected && !allSelected;
  });

  constructor() {
    this.setupSubscriptions();
    this.loadUsers();
  }

  /** Inicializa la selección con usuarios pre-seleccionados */
  public initializeSelection(users: MasterUser[]): void {
    const map = new Map<string, MasterUser>();
    users.forEach(u => map.set(String(u._id), u));
    this.selectedUsersMap.set(map);
  }

  /** Actualiza la selección desde el SmartGrid */
  public setSelectedUsers(users: MasterUser[]): void {
    const map = new Map<string, MasterUser>();
    users.forEach(u => map.set(String(u._id), u));
    this.selectedUsersMap.set(map);
  }

  private setupSubscriptions(): void {
    this.setSearch$.pipe(
      takeUntilDestroyed(),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(search => {
      this.searchTerm.set(search);
      this.currentPage.set(1);
      this.loadUsers();
    });

    this.setPage$.pipe(takeUntilDestroyed())
      .subscribe(({ pageIndex, pageSize }) => {
        this.currentPage.set(pageIndex + 1);
        this.pageSize.set(pageSize);
        this.loadUsers();
      });

    this.toggleUser$.pipe(takeUntilDestroyed())
      .subscribe(user => this.toggleUserSelection(user));

    this.toggleAllPage$.pipe(takeUntilDestroyed())
      .subscribe(users => this.toggleAllPageSelection(users));

    this.clearSelection$.pipe(takeUntilDestroyed())
      .subscribe(() => this.selectedUsersMap.set(new Map()));

    this.retry$.pipe(takeUntilDestroyed())
      .subscribe(() => this.loadUsers());
  }

  private toggleUserSelection(user: MasterUser): void {
    const newMap = new Map(this.selectedUsersMap());
    const id = String(user._id);
    if (newMap.has(id)) {
      newMap.delete(id);
    } else {
      newMap.set(id, user);
    }
    this.selectedUsersMap.set(newMap);
  }

  private toggleAllPageSelection(users: MasterUser[]): void {
    const newMap = new Map(this.selectedUsersMap());
    const allSelected = users.every(u => newMap.has(String(u._id)));

    if (allSelected) {
      users.forEach(u => newMap.delete(String(u._id)));
    } else {
      users.forEach(u => newMap.set(String(u._id), u));
    }
    this.selectedUsersMap.set(newMap);
  }

  private loadUsers(): void {
    this.loading.set(true);
    this.errorState.set(null);

    // Use method that filters only users with business_partner_id
    this.userMasterService.getAllPaginateUsersWithBusinessPartner({
      search: this.searchTerm() || undefined,
      currentPage: this.currentPage(),
      limitPerPage: this.pageSize(),
      sort: 'user_login_username',
      sortDirection: 'ASC'
    }).pipe(
      catchError(err => {
        this.errorState.set(err);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false))
    ).subscribe(response => {
      this.usersData.set(response?.data ?? []);
      this.totalItems.set(response?.pageInfo?.totalCount ?? 0);
    });
  }
}
